import React, { useState, useEffect } from "react";
import AIOfferLetterGenerator from "./AIOfferLetterGenerator";
import UploadOfferLetter from "./UploadOfferLetter";
import OfferLetterPreviewPanel from "./OfferLetterPreviewPanel";
import OfferLetterViewPanel from "./OfferLetterViewPanel";
import { useCommonApi } from "src/_common/hooks/actions/common/appCommonApiHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { API_BASE_URL } from "src/_config";

interface FormData {
  candidateName: string;
  jobTitle: string;
  department: string;
  companyName: string;
  startDate: string;
  salary: string;
  currencyType: string;
  employmentType: string;
  benefits: string;
  includeDigitalSignature: boolean;
  workplace: string;
  workingHours: string;
  reportingTo: string;
}

interface OfferStatusProps {
  selectedCandidate: {
    id: number;
    name: string;
    position: string;
    bestMatch: boolean;
    offerStatus: {
      sent: { date: string; completed: boolean };
      accepted: { waiting: boolean; completed: boolean };
      declined: { notApplicable: boolean; completed: boolean; waiting?: boolean };
      status?: number;
    };
    fullCandidateData: any;
  };
  fetchFinalDecisionCandidates?: () => void;
}

export default function OfferStatus({ selectedCandidate, fetchFinalDecisionCandidates }: OfferStatusProps) {
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);
  const [showViewPanel, setShowViewPanel] = useState(false);
  const [previewFormData, setPreviewFormData] = useState<FormData | null>(null);
  const [previewCurrencyType, setPreviewCurrencyType] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [offerLetterContent, setOfferLetterContent] = useState<string>("");
  const { t } = useTranslation("job-details");
  const toast = useToaster();

  const { callGenerateOfferLetter, callSendOfferLetter, callSendOfferLetterFile } = useCommonApi();

  // Close all forms when the selected candidate changes
  useEffect(() => {
    setShowOfferForm(false);
    setShowUploadForm(false);
    setShowPreviewPanel(false);
    setShowViewPanel(false);
    setPreviewFormData(null);
  }, [selectedCandidate.id]);

  const handleGenerateOfferClick = () => {
    setShowOfferForm(!showOfferForm);
    setShowUploadForm(false); // Close upload form if open
  };

  const handleUploadOfferClick = () => {
    setShowUploadForm(!showUploadForm);
    setShowOfferForm(false); // Close generator form if open
  };

  const handleViewOfferClick = () => {
    console.log("Viewing offer letter for candidate:", selectedCandidate?.fullCandidateData);
    if (selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.offer_letter_content) {
      setOfferLetterContent(selectedCandidate.fullCandidateData?.offer_letter_logs?.[0].offer_letter_content);
      setShowViewPanel(true);
    } else if (selectedCandidate?.fullCandidateData?.offer_letter_logs?.[0]?.offer_letter_file) {
      window.open(
        API_BASE_URL.replace("api/v1/", "") +
          selectedCandidate.fullCandidateData?.offer_letter_logs?.[0].offer_letter_file,
        "_blank"
      );
    }
  };

  const handleGenerateOffer = async (formData: FormData, currencyType: string) => {
    setIsGenerating(true);

    const requestData = {
      type: 2,
      screening_id: selectedCandidate.id.toString(),
      candidate_name: formData.candidateName,
      job_title: formData.jobTitle,
      department: formData.department,
      company_name: formData.companyName,
      start_date: formData.startDate,
      salary: formData.salary,
      currency_type: currencyType,
      employment_type: formData.employmentType,
      benefits: formData.benefits,
      digital_signature: formData.includeDigitalSignature,
    };

    callSendOfferLetter(
      requestData,
      (message: string, response: any) => {
        setIsGenerating(false);
        setShowOfferForm(false); // Close the form
        fetchFinalDecisionCandidates?.();
      },
      (error: string) => {
        setIsGenerating(false);
        toast.error(error);
      }
    );
  };

  const handlePreviewAndSend = async (formData: FormData, currencyType: string) => {
    // Store form data and currency type, clear previous content, then open preview panel
    setPreviewFormData({ ...formData, currencyType });
    setPreviewCurrencyType(currencyType);
    setOfferLetterContent(""); // Clear previous content to regenerate fresh HTML
    setShowPreviewPanel(true);
  };

  const handleSendOfferFromPreview = async () => {
    if (!previewFormData) return;

    setIsGenerating(true);

    const requestData = {
      type: 1,
      screening_id: selectedCandidate.id.toString(),
      offer_letter_content: offerLetterContent || "",
      digital_signature: previewFormData.includeDigitalSignature,
      currency_type: previewCurrencyType,
    };

    callSendOfferLetter(
      requestData,
      (message: string, response: any) => {
        setIsGenerating(false);
        setShowPreviewPanel(false);
        setPreviewFormData(null);
        setPreviewCurrencyType("");
        setShowOfferForm(false); // Close the form
        fetchFinalDecisionCandidates?.();
        toast.success(t("jobDetails.finalDecision.offerStatus.sendSuccess"));
      },
      (error: string) => {
        setIsGenerating(false);
      }
    );
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);

    const formData = new FormData();
    formData.append("screening_id", selectedCandidate.id.toString());
    formData.append("offer_letter_file", file);

    try {
      callSendOfferLetterFile(
        formData,
        (message: string, response: any) => {
          toast.success(t("jobDetails.finalDecision.offerStatus.uploadSuccess"));
          console.log("Uploaded offer letter:", response);
          setIsUploading(false);
        },
        (error: string) => {
          console.error("Error uploading offer letter:", error);
          toast.error(t("jobDetails.finalDecision.offerStatus.uploadError"));
          setIsUploading(false);
        }
      );
    } catch (error) {
      console.error("Error uploading offer letter:", error);
      toast.error(t("jobDetails.finalDecision.offerStatus.uploadError"));
      setIsUploading(false);
    }
  };

  const handleUploadCancel = () => {
    setShowUploadForm(false);
  };

  const handleSendUploadedFile = async (file: File) => {
    setIsUploading(true);

    const formData = new FormData();
    formData.append("screening_id", selectedCandidate.id.toString());
    formData.append("offer_letter_file", file);

    callSendOfferLetterFile(
      formData,
      (message: string, response: any) => {
        toast.success(t("jobDetails.finalDecision.offerStatus.sendSuccess"));
        console.log("Sent uploaded offer letter:", response);
        setShowUploadForm(false);
        setIsUploading(false);
        fetchFinalDecisionCandidates?.();
      },
      (error: string) => {
        console.error("Error sending offer letter:", error);
        toast.error(t("jobDetails.finalDecision.offerStatus.sendError"));
        setIsUploading(false);
      }
    );
  };

  useEffect(() => {
    console.log("Selected Candidate Offer Status:", selectedCandidate);
  }, [selectedCandidate]);

  return (
    <>
      {/* Offer Action Buttons */}
      {!selectedCandidate?.offerStatus?.status && (
        <div className="offer-actions">
          <button className="btn-get-hire" onClick={handleGenerateOfferClick}>
            <i className="fa fa-magic"></i>
            {t("jobDetails.finalDecision.offerStatus.generateOffer")}
          </button>
          <button
            className="btn-reject"
            style={{ backgroundColor: "#fff", color: "#ef4444", border: "1px solid #ef4444" }}
            onClick={handleUploadOfferClick}
          >
            <i className="fa fa-upload"></i>
            {t("jobDetails.finalDecision.offerStatus.uploadOffer")}
          </button>
        </div>
      )}

      {(selectedCandidate?.offerStatus?.status == 6 ||
        selectedCandidate?.offerStatus?.status == 7 ||
        selectedCandidate?.offerStatus?.status == 8) && (
        <div className="offer-actions">
          <button className="btn-get-hire" onClick={handleViewOfferClick}>
            {t("jobDetails.finalDecision.offerStatus.viewOffer")}
          </button>
        </div>
      )}

      {/* AI Offer Letter Generator Form */}
      {showOfferForm && (
        <AIOfferLetterGenerator
          candidateData={{
            name: selectedCandidate.name,
            position: selectedCandidate.position,
          }}
          // onGenerate={handleGenerateOffer}
          onPreviewAndSend={handlePreviewAndSend}
          isGenerating={isGenerating}
        />
      )}

      {/* Upload Offer Letter Form */}
      {showUploadForm && selectedCandidate && (
        <UploadOfferLetter
          candidateData={{
            name: selectedCandidate.name,
            position: selectedCandidate.position,
          }}
          onUpload={handleFileUpload}
          onCancel={handleUploadCancel}
          onSendToCandidate={handleSendUploadedFile}
          isUploading={isUploading}
        />
      )}

      {/* Offer Letter Preview Panel */}
      {showPreviewPanel && previewFormData && selectedCandidate && (
        <OfferLetterPreviewPanel
          isOpen={showPreviewPanel}
          onClose={() => {
            setShowPreviewPanel(false);
            setOfferLetterContent(""); // Clear content on close
          }}
          formData={previewFormData}
          candidateId={selectedCandidate.id}
          onSendToCandidate={handleSendOfferFromPreview}
          offerLetterContent={offerLetterContent}
          setOfferLetterContent={setOfferLetterContent}
          candidateData={selectedCandidate}
        />
      )}

      {/* Offer Letter View Panel */}
      {showViewPanel && selectedCandidate && (
        <OfferLetterViewPanel
          isOpen={showViewPanel}
          onClose={() => setShowViewPanel(false)}
          offerLetterContent={offerLetterContent}
          jobTitle={selectedCandidate.position}
          status={selectedCandidate?.offerStatus?.status}
        />
      )}

      <div className="offer-status">
        <h3>{t("jobDetails.finalDecision.offerStatus.title")}</h3>
        <div className="status-timeline">
          <div className={`status-step ${selectedCandidate.offerStatus.sent.completed ? "completed" : ""}`}>
            <div className="status-icon">
              {selectedCandidate.offerStatus.sent.completed ? <i className="fa fa-check"></i> : <span>1</span>}
            </div>
            <div className="status-info">
              <h4>{t("jobDetails.finalDecision.offerStatus.timeline.offerSent")}</h4>
              <p>
                {selectedCandidate.offerStatus.sent.date || t("jobDetails.finalDecision.offerStatus.timeline.notSent")}
              </p>
            </div>
          </div>

          <div className={`status-step ${selectedCandidate.offerStatus.accepted.completed ? "completed" : "inactive"}`}>
            <div className="status-icon">
              {selectedCandidate.offerStatus.accepted.completed ? <i className="fa fa-check"></i> : <span>2</span>}
            </div>
            <div className="status-info">
              <h4>{t("jobDetails.finalDecision.offerStatus.timeline.accepted")}</h4>
              <p>
                {selectedCandidate.offerStatus.accepted.completed
                  ? t("jobDetails.finalDecision.offerStatus.timeline.offerAccepted")
                  : selectedCandidate.offerStatus.accepted.waiting
                  ? t("jobDetails.finalDecision.offerStatus.timeline.waitingResponse")
                  : t("jobDetails.finalDecision.offerStatus.timeline.notApplicable")}
              </p>
            </div>
          </div>

          <div className={`status-step ${selectedCandidate.offerStatus.declined.completed ? "completed" : "inactive"}`}>
            <div className="status-icon">
              {selectedCandidate.offerStatus.declined.completed ? <i className="fa fa-check"></i> : <span>3</span>}
            </div>
            <div className="status-info">
              <h4>{t("jobDetails.finalDecision.offerStatus.timeline.declined")}</h4>
              <p>
                {selectedCandidate.offerStatus.declined.completed
                  ? t("jobDetails.finalDecision.offerStatus.timeline.offerDeclined")
                  : !selectedCandidate.offerStatus.declined.notApplicable &&
                    selectedCandidate.offerStatus.sent.completed
                  ? t("jobDetails.finalDecision.offerStatus.timeline.waitingResponse")
                  : t("jobDetails.finalDecision.offerStatus.timeline.notApplicable")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
